import React, { useState, useRef, memo, useEffect } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import { useTranslation } from 'react-i18next'
import isEmpty from 'lodash/isEmpty'

import ContentBlock, { ExpandableBlocks } from 'components/ContentBlock'
import GruberDispenserConfig from './GruberDispenserConfig'
import { notEmpty } from 'helpers/validators'
import { Form, Text, Select } from 'components/forms'
import ClientSelector from 'components/forms/ClientSelector'
import prepareMutationData from 'helpers/fetchedDataToForm'
import { useQuery, useMutation } from 'components/graphql'
import getDashboardDSGeo from 'gql/query/getDashboardDSGeo'
import createDashboardDSGeo from 'gql/query/createDashboardDSGeo'
import { ConfirmModal } from 'components/Modal'
import { Loading } from 'media/icons'

import OpeningHours from './OpeningHours'
import ShopTimeSlot from './ShopTimeSlot'
import { Accounting, useAccountingData } from './Accounting'
import { prepareFullMutationData } from './Accounting/prepareMutationData'
import getAccountingLabels from '../../gql/getAccountingLabels'
import getShop from '../../gql/getShop'
import updateShop from '../../gql/updateShop'
import getShopCRs from '../../gql/getShopCRs'


const objectCheck = (obj) => (typeof obj === 'object' && obj !== null && Object.values(obj).length) || null

const Shop = (props) => {
	const { formData: rawFormData, mutations, itemId, appData } = props
	let formData = rawFormData
	const { data: getShopData, loading: shopLoading } = useQuery(getShop, { variables: { id: props.data.id }, fetchPolicy: 'network-only' })
	const data = getShopData?.getShop
	const [t] = useTranslation()
	const [googleMapUrl, setGoogleMapUrl] = useState(formData.googleMapUrl)
	const formRef = useRef({})
	const { data: dsGeoData } = useQuery(getDashboardDSGeo, { variables: { branch: Number(itemId) } })
	const { data: filthyAccountingLabels } = useQuery(getAccountingLabels, { fetchPolicy: 'cache-first' })
	const accountingLabels = filthyAccountingLabels?.getAppConfig

	const { data: shopCashRegisters } = useQuery(getShopCRs, { variables: { id: props.data.id } })
	const shopCashRegisterList = get(shopCashRegisters, 'getShop.cashRegisters', [])
	const [createGeoData, { data: updatedGeoData, loading }] = useMutation(createDashboardDSGeo)

	const accountingOptions = [
		{ value: 1, label: 'individuell' },
		{ value: 2, label: 'SKR 03' },
		{ value: 3, label: 'SKR 04' }
	]

	useEffect(() => {
		if(!loading && !isEmpty(updatedGeoData)) {
			formRef.current.setValue('googleMapUrl', updatedGeoData.createDashboardDSGeo?.google_map_url)
		}
	}, [loading])

	const handleSlotChange = vals => {
		formRef.current.setValue('openingHours', vals)
		formRef.current.submitForm()
	}

	const handleSubmit = vals => {
		if (vals.googleMapUrl !== googleMapUrl) {
			createGeoData({
				variables: {
					branch: Number(itemId),
					googleMapUrl: vals.googleMapUrl
				}
			})
		}
		setGoogleMapUrl(vals.googleMapUrl)
		delete vals.googleMapUrl
		const { openingHours } = vals
		if (Array.isArray(openingHours)) {
			let optHours = [...get(data, 'openingHours', [])]
			const updatingSlots = []
			openingHours.forEach(item => {
				const { id, ...restVals } = item
				const isDeleting = !Object.keys(restVals).length
				const updatingItem = isDeleting
					? { id, isDeleted: true }
					: prepareMutationData(restVals)
				updatingSlots.push(updatingItem)
				if (!id) {
					if (!restVals.__typename) {
						restVals.__typename = 'ClientOpeningHours'
					}
					if (!restVals.openingTime.__typename) {
						restVals.openingTime.__typename = 'WorkTimeInterval'
					}
					optHours.push(restVals)
				} else if (!isDeleting) {
					optHours = optHours.map(oldItem => {
						if (Number(oldItem.id) === Number(id)) {
							return { id, ...restVals }
						}
						return oldItem
					})
				} else {
					optHours = optHours.filter(oldItem => Number(oldItem.id) !== Number(id))
				}
			})
			mutations.updateItem({ ...vals, openingHours: updatingSlots })
		} else {
			mutations.updateItem(vals)
		}
	}

	if (dsGeoData && dsGeoData.getDashboardDSGeo) {
		const [geoItem] = dsGeoData.getDashboardDSGeo
		if (geoItem) {
			formData = { ...formData, googleMapUrl: geoItem.google_map_url }
		}
	}

	const ranges = get(data, 'openingHours', []).filter(({ timeSlot }) => timeSlot || timeSlot === 0)
	const openingHoursData = get(data, 'openingHours', []).filter(({ flag }) => [1, 2, 3, 4, 5, 6, 7, 99].includes(flag))

	const [handleUpdateShop] = useMutation(updateShop)

	const handleChangeData = (value) => {
		const valueToSet = prepareFullMutationData(value)
		handleUpdateShop({
			variables: {
				fields: valueToSet,
				id: data.id
			},
			awaitRefetchQueries: true,
			refetchQueries: [{ query: getShop, variables: { id: data.id } }]
		})
	}

	const handleUpdateSavedChartOfAccounts = async (value) => {
		const showConfirm = (confirmOptions = {}) => {
			ConfirmModal(confirmOptions).then(
				() => handleChangeData(value),
				() => null
			)
		}
		showConfirm()
	}

	const accountingData = useAccountingData(data, accountingLabels, objectCheck, t, data?.savedChartOfAccounts)

	return (
		<>
			<ContentBlock>
				<Form
					smartSubmit
					initialValues={formData}
					onSubmit={handleSubmit}
					getApi={api => {
						formRef.current = api
					}}
				>
					<Row>
						<Col xs={5}>
							<ClientSelector lastNode='REGION' field='regionId' {...notEmpty()} />
							<Text field='name' label={t('options_section.fields.shop_name')} {...notEmpty()} />
							<Text field='openingHours' hidden />
							{(appData.e2nIntegrationActive || appData?.cashRegisterGlobal.e2nReportingActive) &&
							<Text label={t('options_section.fields.e2n_api_key')} field='timeTrackingToken' />
							}
							{dsGeoData.getDashboardDSGeo &&
								<Text label={t('options_section.fields.google_map_url')} field='googleMapUrl' />
							}
						</Col>
						<Col xs={5}>
							<Select
								field='clickCollectCashRegisterId'
								label={t('options_section.fields.click_collect_cash_register')}
								options={shopCashRegisterList}
								valueField='id'
								labelField='name'
								isSearchable
								isClearable
							/>
							<Select
								field='qrMenuCashRegisterId'
								label={t('options_section.fields.qr_menu_cash_register')}
								options={shopCashRegisterList}
								valueField='id'
								labelField='name'
								isSearchable
								isClearable
							/>
							{appData.isMasterPrintActivated && <Select
								field='kitchenMasterClientId'
								label={t('options_section.fields.kitchenMasterClientId')}
								options={shopCashRegisterList.filter(cr => cr.deviceType === 'MOBILE_WAITER')}
								valueField='id'
								labelField='name'
								isClearable
								isSearchable
							/>}
							{appData.isShopFieldActive &&
								<Text
									label={t('options_section.fields.shop_number_prefix')}
									field='codePrefix'
									validate={v => (/^\d{1,3}\.$/.test(v) || !v) ? null : t('options_section.strings.error_validate_shop_codePrefix')}
								/>
							}
						</Col>
					</Row>
					<ExpandableBlocks>
						<ContentBlock label={t('options_section.strings.eguma')}>
							<Text type='password' disablePasswordAutoComplete label={t('options_section.fields.egumaApiKey')} field='egumaApiKey'/>
						</ContentBlock>
						<ContentBlock label={t('options_section.strings.gurado')}>
							<Text label={t('options_section.fields.guradoConsumerKey')} field='guradoConsumerKey'/>
							<Text type='password' disablePasswordAutoComplete label={t('options_section.fields.guradoConsumerSecret')} field='guradoConsumerSecret'/>
						</ContentBlock>
						{appData?.cashRegisterGlobal?.isAxessVoucherSystemEnabled && (
							<ContentBlock label={t('options_section.strings.axess')}>
								<Text type='password' disablePasswordAutoComplete label={t('options_section.fields.axessApiKey')} field='axessApiKey'/>
							</ContentBlock>
						)}
					</ExpandableBlocks>
				</Form>
			</ContentBlock>
			<ExpandableBlocks>
				{appData?.cashRegisterGlobal?.isGruberDispenserEnabled && (
					<ContentBlock label={t('options_section.strings.gruber_dispenser')}>
						<GruberDispenserConfig
							shopId={data?.id}
							gruberConfig={data?.gruberConfig || []}
							shopCashRegisterList={shopCashRegisterList}
							loading={shopLoading}
						/>
					</ContentBlock>
				)}
				<ContentBlock label={t('options_section.strings.accounting')}>
					<>
						{objectCheck(accountingData)
							? (
								<>
									{appData.countryId === '2' &&
										<Row end='xs' style={{ margin: '0' }}>
											<Select
												noForm
												simpleValue={true}
												value={data?.savedChartOfAccounts}
												onChange={(e) => handleUpdateSavedChartOfAccounts(e.value)}
												options={accountingOptions}
												label={t('common.strings.chartOfAccountsDatev')}
											/>
										</Row>
									}
									<Accounting
										key={data?.savedChartOfAccounts}
										data={accountingData || {}}
										countryId={appData.countryId}
										updateMutation={mutations.updateItem}
										objectCheck={objectCheck}
										disabled={data?.savedChartOfAccounts !== 1}
									/>
								</>
							)
							: (
								<Loading className='loading-ico' />
							)
						}
					</>
				</ContentBlock>
				<ContentBlock label={t('options_section.strings.openingHours')}>
					<OpeningHours
						ranges={openingHoursData}
						onChange={handleSlotChange}
					/>
				</ContentBlock>
				<ContentBlock label={t('options_section.strings.time_ranges_for_unattended_systems')}>
					<ShopTimeSlot
						ranges={ranges}
						onChange={handleSlotChange}
					/>
				</ContentBlock>
			</ExpandableBlocks>
		</>
	)
}

const propsAreEqual = (prevProps, nextProps) => {
	return isEqual(prevProps.formData, nextProps.formData)
}

export default memo(Shop, propsAreEqual)
